/**
 * `roundingMode` lives inside the `RiskScoringModelConfig` blob, so the raw
 * JSON editor offers the same value the rounding-mode dropdown disables for
 * anyone who cannot run a recalculation (RSP-7277). This restores the persisted
 * mode over whatever a user in that position submitted, so the two controls
 * agree.
 *
 * **This is not the security boundary.** The config is written through a route
 * whose permission org Admins already hold, with a direct-to-Hasura fallback on
 * the V2 lane, so anything bypassing the browser bypasses this too. RSP-7277's
 * third criterion is a server-side refusal — a diff check in
 * `services/data-layer` plus the Hasura permission — and it is still required.
 * What this closes is the browser contradicting itself.
 */
const parseObject = (raw: string): Record<string, unknown> | null => {
  try {
    const parsed: unknown = JSON.parse(raw);

    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

export interface PreservedRoundingMode {
  /** The config to save — `submitted` unchanged unless the mode was altered. */
  config: string;
  /** True when the submitted mode was overridden, so the caller can say so. */
  changed: boolean;
}

export const preserveRoundingMode = (
  submitted: string,
  persisted: string
): PreservedRoundingMode => {
  const submittedConfig = parseObject(submitted);
  // Unparseable or non-object JSON carries no mode to override, and rewriting
  // it would replace the user's text with a guess about what they meant.
  if (!submittedConfig) {
    return { config: submitted, changed: false };
  }

  const persistedMode = parseObject(persisted)?.roundingMode;
  const submittedMode = submittedConfig.roundingMode;
  if (submittedMode === persistedMode) {
    return { config: submitted, changed: false };
  }

  const restored = { ...submittedConfig };
  if (persistedMode === undefined) {
    // Nothing was persisted, so the field itself is the change to undo —
    // leaving `roundingMode: undefined` would serialise the key away anyway,
    // but deleting states the intent.
    delete restored.roundingMode;
  } else {
    restored.roundingMode = persistedMode;
  }

  return { config: JSON.stringify(restored, null, 2), changed: true };
};
